import { Add, AssessmentOutlined } from '@mui/icons-material'
import { Box, useTheme } from '@mui/material'
import { Context, memo } from 'react'
import { useTranslation } from 'react-i18next'
import FormSchemasDialog from 'src/components/overallReport/FormSchemasDialog'
import { IMainComponentType } from 'src/enums'
import { permissionValue } from 'src/enums/permissionValue'
import { ILocality } from 'src/generated-types'
import { formFieldsDefaultValues } from 'src/generated-types/form-field'
import { formSchemaReportTypes } from 'src/generated-types/form-schema'
import { IMainComponent } from 'src/generated-types/main-component'
import { useDialogCtx } from 'src/hooks/context-hooks'
import i18n from 'src/i18n'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { getCageLetter } from 'src/utils/function'
import EquipmentSectionLayout from './EquipmentSectionLayout'
import ReportInEquipment from './ReportsInEquipment'

export const REPORT_MAX_VISIBLE_ROWS = 10

interface Props {
  context: Context<any> | null | undefined
  customerId: EntityId
  localityId: EntityId | undefined
  mainComponentId: any
  mainComponent: IMainComponent | undefined
  locality: ILocality | undefined
  isReadOnly?: boolean
  documentCount: number
  onReportCountChange: (length: number) => void
  height: number
}

const ReportSection = memo((props: Props) => {
  const {
    context,
    mainComponent,
    mainComponentId,
    customerId,
    localityId,
    locality,
    isReadOnly,
    documentCount,
    onReportCountChange,
  } = props
  const { showDialog } = useDialogCtx()
  const { t } = useTranslation(['main-component'])
  const theme = useTheme()

  const onAddReport = async () => {
    showDialog(
      FormSchemasDialog,
      {
        componentProps: {
          customerId: customerId,
          localityId: localityId as any,
          property: {
            mainComponentId: mainComponentId,
            type: formSchemaReportTypes.MainComponent,
          },
          formInitialValues: {
            [formFieldsDefaultValues.orderNumber]: mainComponent?.orderNumber || '',
            [formFieldsDefaultValues.netType]:
              Object.keys(IMainComponentType)[
                Object.values(IMainComponentType).indexOf(
                  mainComponent?.type.componentType as unknown as IMainComponentType,
                )
              ] || '',
            [formFieldsDefaultValues.siteLocationName]: locality?.name ? locality?.name : 'n/a',
            [formFieldsDefaultValues.facilityName]: locality?.name ? locality?.name : 'n/a',
            [formFieldsDefaultValues.siteNumber]: locality?.locationNr ? locality?.locationNr : 'n/a',
            [formFieldsDefaultValues.resourcesName]:
              `${JSON.parse(mainComponent?.type.name ?? '')[i18n.language]} - ${mainComponent?.serialNumber}` || '',
            [formFieldsDefaultValues.PositionNumber]: mainComponent?.position?.id?.toString() || '',
            [formFieldsDefaultValues.cagePlacement]: `${getCageLetter(mainComponent?.position?.name ?? '')} ${
              mainComponent?.position?.reference ? `(${mainComponent?.position?.reference})` : ''
            }`,
          },
        },
        opts: {
          maxWidth: 'md',
          fullWidth: true,
        },
      },
      undefined,
      true,
    )
  }

  return (
    <Box height="100%">
      <EquipmentSectionLayout
        title={t('reports', { ns: 'common' })}
        icon={<AssessmentOutlined sx={{ fontSize: '18px', color: getThemeColor(theme, MUIThemeColors.primaryMain) }} />}
        buttonText={t('new-form', { ns: 'overall-reports' })}
        buttonIcon={<Add />}
        sx={{ paddingBottom: '0px' }}
        onButtonClick={onAddReport}
        context={context}
        permissions={[
          permissionValue.Locality.MainComponent.Document.Write,
          permissionValue.ClientAdminPermission,
          permissionValue.ServiceStation.Write,
          permissionValue.ClientAdminPortal.Equipment_Portal,
        ]}
        content={
          <ReportInEquipment
            {...props}
            height="100%"
            autoHeight={false}
            maxVisibleRowCount={REPORT_MAX_VISIBLE_ROWS}
            minimalEmptyStatus={true}
            setDataLength={onReportCountChange}
            isReadOnly={isReadOnly}
          />
        }
        isReadOnly={isReadOnly}
      />
    </Box>
  )
})

export default ReportSection
